import React from 'react';
import { Mail, Phone, MapPin, Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';

export const Footer = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <footer className="bg-black/50 backdrop-blur-xl border-t border-white/10 py-20" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <img 
              src="/agatsya-logo.png" 
              alt="Agatsya International" 
              className="h-24 w-auto mb-4"
            />
            <p className="text-gray-400 leading-relaxed">
              Bridging Global Trade, Industrial Innovation, and Financial Excellence.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-gold font-semibold uppercase tracking-wider mb-6 text-sm">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => scrollToSection('home')}
                  className="text-gray-400 hover:text-gold transition-colors"
                  data-testid="footer-link-home"
                >
                  Home
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('export')}
                  className="text-gray-400 hover:text-gold transition-colors"
                  data-testid="footer-link-export"
                >
                  Export Products
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('financial')}
                  className="text-gray-400 hover:text-gold transition-colors"
                  data-testid="footer-link-financial"
                >
                  Financial Services
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('consultancy')}
                  className="text-gray-400 hover:text-gold transition-colors"
                  data-testid="footer-link-consultancy"
                >
                  Consultancy
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('about')}
                  className="text-gray-400 hover:text-gold transition-colors"
                  data-testid="footer-link-about"
                >
                  About Us
                </button>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-gold font-semibold uppercase tracking-wider mb-6 text-sm">Our Services</h4>
            <ul className="space-y-3 text-gray-400">
              <li>Agricultural Exports</li>
              <li>Chemical Division</li>
              <li>Loans</li>
              <li>Taxation Services</li>
              <li>GST Registration</li>
              <li>Export Licensing</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-gold font-semibold uppercase tracking-wider mb-6 text-sm">Contact Info</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail size={20} className="text-gold flex-shrink-0 mt-1" />
                <a
                  href="mailto:admin@agatsyainternational.com"
                  className="text-gray-400 hover:text-gold transition-colors text-sm"
                >
                  admin@agatsyainternational.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone size={20} className="text-gold flex-shrink-0 mt-1" />
                <a
                  href="tel:+919225059195"
                  className="text-gray-400 hover:text-gold transition-colors text-sm"
                >
                  +91 9225059195
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={20} className="text-gold flex-shrink-0 mt-1" />
                <span className="text-gray-400 text-sm">
                  16/1185, Behind Annaram Gonda School, Ichalkaranji, Kolhapur, Maharashtra - 416115
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-white/10 my-8" />

        {/* Bottom Section */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Copyright */}
          <p className="text-gray-400 text-sm text-center md:text-left">
            &copy; {new Date().getFullYear()} Agatsya International. All rights reserved.
          </p>

          {/* Social Links */}
          <div className="flex items-center gap-4">
            <a
              href="https://www.facebook.com/profile.php?id=61587483883075"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-white/5 border border-white/10 hover:border-gold/50 flex items-center justify-center text-gray-400 hover:text-gold transition-all"
              aria-label="Facebook"
              data-testid="social-facebook"
            >
              <Facebook size={18} />
            </a>
            <a
              href="https://x.com/agatsyaintl"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-white/5 border border-white/10 hover:border-gold/50 flex items-center justify-center text-gray-400 hover:text-gold transition-all"
              aria-label="Twitter"
              data-testid="social-twitter"
            >
              <Twitter size={18} />
            </a>
            <a
              href="https://www.linkedin.com/in/abhijit-vyas-3a29b33a9/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-white/5 border border-white/10 hover:border-gold/50 flex items-center justify-center text-gray-400 hover:text-gold transition-all"
              aria-label="LinkedIn"
              data-testid="social-linkedin"
            >
              <Linkedin size={18} />
            </a>
            <a
              href="https://www.instagram.com/agatsyainternational/?hl=en"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-white/5 border border-white/10 hover:border-gold/50 flex items-center justify-center text-gray-400 hover:text-gold transition-all"
              aria-label="Instagram"
              data-testid="social-instagram"
            >
              <Instagram size={18} />
            </a>
          </div>

          {/* Website */}
          <a
            href="https://www.agatsyainternational.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 hover:text-gold transition-colors text-sm"
          >
            www.agatsyainternational.com
          </a>
        </div>
      </div>
    </footer>
  );
};
